if (!customElements.get('country-selector')) {
  class CountrySelector extends HTMLElement {
    constructor() {
      super();
      this.provinceElement = this.querySelector('.js-shipping-province');
      this.countryElement = this.querySelector('.js-shipping-country');
      this.provinceWrapper = this.querySelector('.js-province-container');
      window.initScriptOnDemand(this, this.init.bind(this));
    }

    init() {
      CountrySelector.selectOption(this.countryElement, this.countryElement.dataset.default);

      this.updateProvinceOptions();

      if (this.provinceElement.dataset.default && this.provinceElement.options.length > 0) {
        CountrySelector.selectOption(this.provinceElement, this.provinceElement.dataset.default);
      }

      this.countryElement.addEventListener('change', this.updateProvinceOptions.bind(this));
    }

    /**
     * Updates the province/state options based on selected country.
     */
    updateProvinceOptions() {
      const selectedCountry = this.countryElement.options[this.countryElement.selectedIndex];
      const provinceList = JSON.parse(selectedCountry.dataset.provinces);

      // Clear existing options in the province field.
      Array.from(this.provinceElement.options).forEach((option) => option.remove());

      if (provinceList && provinceList.length === 0) {
        this.provinceWrapper.hidden = true;
      } else {
        provinceList.forEach((province) => {
          const provinceOption = document.createElement('option');
          [provinceOption.value, provinceOption.innerHTML] = province;
          this.provinceElement.appendChild(provinceOption);
        });

        this.provinceWrapper.hidden = false;
      }
    }

    /**
     * Sets the selected option of a <select> element.
     * @param {Element} dropdown - Country or province <select> element.
     * @param {string} value - The value to be selected in the dropdown.
     */
    static selectOption(dropdown, value) {
      Array.from(dropdown.options).forEach((option, index) => {
        if (option.value === value || option.innerHTML === value) {
          dropdown.selectedIndex = index;
        }
      });
    }
  }

  customElements.define('country-selector', CountrySelector);
}

if (!customElements.get('shipping-calculator')) {
  class ShippingCalculator extends HTMLElement {
    constructor() {
      super();
      this.submitButton = this.querySelector('button');
      this.shippingForm = this.querySelector('form');
      this.shippingForm.addEventListener('submit', this.processFormSubmit.bind(this));
    }

    initialize() {
      this.countryField = this.querySelector('.js-shipping-country');
      this.provinceField = this.querySelector('.js-shipping-province');
      this.cityField = this.querySelector('.js-city-input');
      this.addressField = this.querySelector('.js-address-input');
      this.zipField = this.querySelector('.js-zip-input');
      this.ratesDisplay = this.querySelector('.js-shipping-rate');
      this.errorDisplay = this.querySelector('.js-shipping-error');
      this.isInitialized = true;
    }

    /**
     * Handles the form submission for calculating shipping rates.
     * @param {object} event - The submit event object.
     */
    async processFormSubmit(event) {
      event.preventDefault();
      if (!this.isInitialized) this.initialize();

      this.errorDisplay.hidden = true;
      this.ratesDisplay.hidden = true;
      this.submitButton.disabled = true;

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.submitButton.querySelector('.loading__spinner').classList.remove('hidden', 'spinning-complete');

      const fields = {
        zip: this.zipField.value.trim(),
        country: this.countryField.value,
        province: this.provinceField.value,
        city: this.cityField?.value.trim(),
        address1: this.addressField?.value.trim()
      };

      try {
        // Ask Shopify for rates on a separate test cart (tokenless Storefront API). The real cart's
        // rate endpoints remember the last address checked, so an undeliverable lookup could break
        // the next valid one and be carried into checkout (showing items as sold out).
        const resultData = await this.getStorefrontRates(fields);
        if (resultData?.errors) {
          this.displayErrors(resultData.errors);
        } else if (resultData) {
          this.displayRates(resultData);
        }
      } catch (error) {
        // E.g. Shopify throttling requests. Don't fall back to the real cart's rate endpoints, which
        // can give a wrong "We do not ship" answer; checkout always shows the real options.
        console.error('Shipping rate lookup failed.', error); // eslint-disable-line
        this.displayRates({ shipping_rates: [] }, window.strings.cart.shippingCalculator.unavailable);
      } finally {
        this.submitButton.disabled = false;
        this.submitButton.setAttribute('aria-disabled', false);
        this.submitButton.classList.remove('loading');
        this.submitButton.querySelector('.loading__spinner').classList.add('hidden', 'spinning-complete');
      }
    }

    /**
     * Gets delivery options for the current cart items via the Storefront API, using a separate
     * test cart so the customer's real cart (and checkout) is never touched. Reflects whatever is
     * configured in Shipping and delivery: local delivery, shipping zones or carrier-calculated rates.
     * @param {object} fields - The address fields entered by the customer.
     * @returns {Promise<object|null>} Rates data in the same shape as shipping_rates.json,
     *   { errors } if the address is invalid (e.g. PIN code doesn't match the state), or null.
     */
    async getStorefrontRates(fields) {
      const countryCodes = JSON.parse(this.dataset.countryCodes || '{}');
      const countryCode = countryCodes[fields.country];
      if (!countryCode) return { shipping_rates: [] }; // Country isn't in any active market.

      const deliveryAddress = { countryCode, zip: fields.zip };
      // Shopify accepts the province name here and converts it to the code.
      if (fields.province) deliveryAddress.provinceCode = fields.province;
      if (fields.city) deliveryAddress.city = fields.city;
      if (fields.address1) deliveryAddress.address1 = fields.address1;

      let cartId = await ShippingCalculator.getTestCartId();
      if (!cartId) return null;

      let result = await this.replaceTestCartAddress(cartId, deliveryAddress);
      if (result.cartNotFound) {
        // The saved test cart has expired; make a new one and try once more.
        ShippingCalculator.saveTestCart(null);
        cartId = await ShippingCalculator.getTestCartId();
        if (!cartId) return null;
        result = await this.replaceTestCartAddress(cartId, deliveryAddress);
      }
      if (result.errors) return { errors: result.errors };

      // Every delivery group (e.g. items from different locations) must be deliverable.
      const groups = result.deliveryGroups.nodes;
      if (!groups.length || groups.some((group) => !group.deliveryOptions.length)) {
        // Once a Shopify cart has had an undeliverable address it keeps returning no delivery
        // options, even for a valid address afterwards, so start a fresh test cart next time.
        ShippingCalculator.saveTestCart(null);
        return { shipping_rates: [] };
      }

      return {
        shipping_rates: groups[0].deliveryOptions.map((option) => ({
          name: option.title,
          price: Number(option.estimatedCost.amount).toFixed(2)
        }))
      };
    }

    /**
     * Checks the address against Shopify's strict address rules (as used at checkout), then sets it
     * on the test cart and returns the delivery groups.
     * @param {string} cartId - The test cart id.
     * @param {object} deliveryAddress - The address being checked.
     * @returns {Promise<object>} { deliveryGroups }, { errors } or { cartNotFound: true }.
     */
    async replaceTestCartAddress(cartId, deliveryAddress) {
      const mutation = (selection) => `mutation ($cartId: ID!, $addresses: [CartSelectableAddressInput!]!) {
        cartDeliveryAddressesReplace(cartId: $cartId, addresses: $addresses) {
          ${selection}
          userErrors { message code field }
        }
      }`;
      const variables = (validationStrategy) => ({
        cartId,
        addresses: [{ address: { deliveryAddress }, selected: true, oneTimeUse: true, validationStrategy }]
      });

      // 1. Strict check. It always fails on fields the calculator doesn't ask for (name, phone,
      //    street, city), which leaves the cart unchanged; only PIN code/state/country errors matter.
      const strict = await ShippingCalculator.storefrontRequest(mutation(''), variables('STRICT'));
      const strictErrors = strict.data?.cartDeliveryAddressesReplace?.userErrors || [];
      if (strictErrors.some((error) => error.code === 'INVALID' && error.field?.includes('cartId'))) return { cartNotFound: true };

      const errors = {};
      strictErrors.forEach((error) => {
        const field = (error.field || []).at(-1);
        if (error.code !== 'ADDRESS_FIELD_IS_REQUIRED' && ['zip', 'provinceCode', 'countryCode'].includes(field)) {
          errors[field] = error.message;
        }
      });
      if (Object.keys(errors).length) return { errors };

      // 2. Set the address and read the delivery options. withCarrierRates (needed for delivery
      //    partner apps) must be requested with @defer.
      const selection = `cart {
        ... @defer {
          deliveryGroups(first: 10, withCarrierRates: true) {
            nodes { deliveryOptions { title estimatedCost { amount } } }
          }
        }
      }`;
      const parts = await ShippingCalculator.storefrontRequest(mutation(selection), variables('COUNTRY_CODE_ONLY'), true);

      let deliveryGroups = null;
      parts.forEach((part) => {
        const payload = part.data?.cartDeliveryAddressesReplace;
                if (payload?.userErrors?.length) throw new Error(payload.userErrors.map((error) => error.message).join(', '));
        if (payload?.cart?.deliveryGroups) deliveryGroups = payload.cart.deliveryGroups;
        (part.incremental || []).forEach((increment) => {
          if (increment.data?.deliveryGroups) deliveryGroups = increment.data.deliveryGroups;
        });
      });
      if (!deliveryGroups) throw new Error('No delivery groups in response');

      return { deliveryGroups };
    }

    /**
     * Returns the id of a test cart holding the same items as the real cart. Shopify limits how many
     * carts can be created, so one test cart is kept for the browser session and only re-created
     * when the items in the real cart change or after an undeliverable address.
     * @returns {Promise<string|null>} The test cart id, or null if the real cart is empty.
     */
    static async getTestCartId() {
      const cart = await (await fetch(`${window.routes.cart_url}.js`)).json();
      if (!cart.items.length) return null;

      const lines = cart.items.map((item) => {
        const line = { merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`, quantity: item.quantity };
        if (item.selling_plan_allocation) {
          line.sellingPlanId = `gid://shopify/SellingPlan/${item.selling_plan_allocation.selling_plan.id}`;
        }
        return line;
      });
      const linesKey = JSON.stringify(lines);

      const saved = ShippingCalculator.loadTestCart();
      if (saved && saved.linesKey === linesKey) return saved.id;

      const result = await ShippingCalculator.storefrontRequest(
        `mutation ($input: CartInput!) { cartCreate(input: $input) { cart { id } userErrors { message } } }`,
        { input: { lines } }
      );
      const id = result.data?.cartCreate?.cart?.id;
      if (!id) throw new Error(JSON.stringify(result.errors || result.data?.cartCreate?.userErrors));

      ShippingCalculator.saveTestCart({ id, linesKey });
      return id;
    }

    static loadTestCart() {
      try {
        return ShippingCalculator.testCart || JSON.parse(sessionStorage.getItem('shipping-calculator-test-cart'));
      } catch (error) {
        return ShippingCalculator.testCart || null;
      }
    }

    static saveTestCart(testCart) {
      ShippingCalculator.testCart = testCart;
      try {
        if (testCart) sessionStorage.setItem('shipping-calculator-test-cart', JSON.stringify(testCart));
        else sessionStorage.removeItem('shipping-calculator-test-cart');
      } catch (error) {
        // Storage unavailable (e.g. private mode); the in-memory copy is enough.
      }
    }

    /**
     * Sends a tokenless Storefront API request from the store's own domain.
     * @param {string} query - The GraphQL query or mutation.
     * @param {object} variables - The GraphQL variables.
     * @param {boolean} deferred - Whether the query uses @defer (multipart response).
     * @returns {Promise<object|object[]>} The JSON result, or the list of parts when deferred.
     */
    static async storefrontRequest(query, variables, deferred = false) {
      const response = await fetch(`/api/${ShippingCalculator.storefrontApiVersion}/graphql.json`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(deferred && { Accept: 'multipart/mixed' }) },
        body: JSON.stringify({ query, variables })
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      // A deferred response arrives as multipart parts, each a JSON object on its own line.
      const parts = (await response.text())
        .split('\n')
        .filter((line) => line.trim().startsWith('{'))
        .map((line) => JSON.parse(line));
      parts.forEach((part) => {
        if (part.errors) throw new Error(JSON.stringify(part.errors));
      });
      return deferred ? parts : parts[0];
    }

    /**
     * Displays the available shipping rates.
     * @param {object} data - The response data from the shipping API.
     * @param {string} [emptyMessage] - Message to show instead of "no rates".
     */
    displayRates(data, emptyMessage) {
      const headingElement = this.ratesDisplay.querySelector('.js-shipping-rate-heading');
      const textElement = this.ratesDisplay.querySelector('.js-shipping-rate-text');
      textElement.innerHTML = '';

      if (data.shipping_rates && data.shipping_rates.length) {
        const headingText = data.shipping_rates.length === 1 ? 'singleRate' : 'multipleRates';
        let rateOptions = '';

        data.shipping_rates.forEach((rate) => {
          const formattedPrice = theme.settings.moneyWithCurrencyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, rate.price);
          rateOptions += `<li>${rate.name}: ${formattedPrice}</li>`;
        });

        headingElement.textContent = window.strings.cart.shippingCalculator[headingText];
        textElement.innerHTML = `<ul class="mt-1 t5">${rateOptions}</ul>`;
        headingElement.hidden = false;
      } else {
        headingElement.hidden = true;
        textElement.innerHTML = emptyMessage || window.strings.cart.shippingCalculator.noRates;
      }

      this.ratesDisplay.hidden = false;
    }

    /**
     * Displays errors related to shipping rate retrieval.
     * @param {object} data - The error data from the shipping API.
     */
    displayErrors(data) {
      let errorMessages = '';

      Object.keys(data).forEach((key) => {
        errorMessages += `<li>${data[key]}</li>`;
      });

      this.errorDisplay.querySelector('.js-shipping-error-content').innerHTML = `<ul class="mt-1 t5">${errorMessages}</ul>`;
      this.errorDisplay.hidden = false;
    }
  }

  ShippingCalculator.storefrontApiVersion = '2025-10';

  customElements.define('shipping-calculator', ShippingCalculator);
}
